import { useMemo, useRef, useState } from 'react';
import { TimerOutlined } from '@mui/icons-material';
import {
  Box,
  ClickAwayListener,
  InputAdornment,
  MenuItem,
  Popper,
  TextField,
  Typography,
} from '@mui/material';
import { StyledMenuItem, commonTextFieldSx, getMenuItemSx } from './utils';

type PickerProps = {
  value: null | string | number | undefined;
  onChange: (val: string) => void;
};

export const DurationPicker: React.FC<PickerProps> = ({ value, onChange }) => {
  const hourOptions = Array.from({ length: 13 }, (_, i) => i.toString().padStart(2, '0'));
  const minuteOptions = Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, '0'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLElement | null>(null);
  const [tempHour, setTempHour] = useState('00');
  const [tempMinute, setTempMinute] = useState('30');

  const displayValue = useMemo(() => {
    if (typeof value === 'string' && value.includes(':')) return value;
    const total = typeof value === 'number' ? value : parseInt(value || '0', 10);
    const h = Math.floor(total / 60)
      .toString()
      .padStart(2, '0');
    const m = (total % 60).toString().padStart(2, '0');
    setTempHour(h);
    setTempMinute(m);
    return `${h}:${m}`;
  }, [value]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    onChange(`${tempHour}:${tempMinute}`);
    setOpen(false);
  };

  return (
    <>
      <TextField
        inputRef={inputRef}
        label='Duration (hh:mm)'
        aria-label='Duration hours and minutes'
        value={displayValue}
        onClick={handleClick}
        variant='outlined'
        fullWidth
        sx={commonTextFieldSx}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position='end'>
              <TimerOutlined sx={{ cursor: 'pointer' }} />
            </InputAdornment>
          ),
        }}
      />
      <Box sx={{ zIndex: 1, position: 'relative' }}>
        <Popper
          open={open}
          anchorEl={anchorEl}
          placement='bottom-start'
          disablePortal
          modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
        >
          <ClickAwayListener onClickAway={handleClose}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 1,
                bgcolor: 'white',
                border: '1px solid #ccc',
                boxShadow: 3,
                p: 1,
                width: inputRef.current?.offsetWidth || 300,
                zIndex: 1300,
              }}
            >
              <Box>
                <Typography variant='caption' fontWeight='bold'>
                  Hours
                </Typography>
                <Box sx={{ maxHeight: 250, overflowY: 'auto' }}>
                  {hourOptions.map((hr) => {
                    const isSelected = hr === tempHour;
                    const isDisabled = hr === '00' && tempMinute === '00';
                    return (
                      <Box
                        key={hr}
                        onClick={() => {
                          if (isDisabled) return;
                          setTempHour(hr);
                          onChange(`${hr}:${tempMinute}`);
                        }}
                        ref={(el) => {
                          if (!isDisabled && isSelected && el instanceof HTMLElement) {
                            el.scrollIntoView({ block: 'center' });
                          }
                        }}
                        sx={{
                          px: 1,
                          py: 1,
                          borderRadius: 1,
                          fontSize: 'medium',
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                          opacity: isDisabled ? 0.5 : 1,
                          pointerEvents: isDisabled ? 'none' : 'auto',
                          ...getMenuItemSx(isSelected),
                        }}
                      >
                        {hr}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
              <Box>
                <Typography variant='caption' fontWeight='bold'>
                  Minutes
                </Typography>
                <Box sx={{ maxHeight: 250, overflowY: 'auto' }}>
                  {minuteOptions.map((min) => {
                    const isSelected = min === tempMinute;
                    const isDisabled = min === '00' && tempHour === '00';
                    return (
                      <Box
                        key={min}
                        onClick={() => {
                          setTempMinute(min);
                          onChange(`${tempHour}:${min}`);
                          setOpen(false);
                        }}
                        ref={(el) => {
                          if (!isDisabled && isSelected && el instanceof HTMLElement) {
                            el.scrollIntoView({ block: 'center' });
                          }
                        }}
                        sx={{
                          px: 1,
                          py: 1,
                          borderRadius: 1,
                          fontSize: 'medium',
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                          opacity: isDisabled ? 0.5 : 1,
                          pointerEvents: isDisabled ? 'none' : 'auto',
                          ...getMenuItemSx(isSelected),
                        }}
                      >
                        {min}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          </ClickAwayListener>
        </Popper>
      </Box>
    </>
  );
};
